import React, { useEffect, useRef } from "react";
import { App as AntdApp } from "antd";
import { useMutation } from "@apollo/client";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useOutletContext } from 'react-router-dom';
import ArticleContentEditor from "../../components/forms/Editor/ArticleContentEditor";
import { UPDATE_CAMPAIGN } from '../../utils/api/campaign';
import { CampaignContentSource, CampaignType } from "../../__generated__/graphql";
import { withAsyncErrorToast } from "../../utils/withToast";
import { getSubmitDisabledReason, serializeFormErrors } from "../../components/forms/utils/formDiagnostics";
import ProviderContent from "../Campaign/ProviderContent";
import { trackEvent } from "../../utils/analytics";

// Friendly names for the required fields, used in the "please complete" message.
const FIELD_LABELS: Record<string, string> = {
  headline: 'Article Title',
  teaser: 'Subtitle',
  contentObject: 'Main Content',
};

const CreateMetaArticle = () => {
  const {campaign} = useOutletContext<any>()

  const [updateCampaign, { loading }] = useMutation(
    UPDATE_CAMPAIGN
  );

  const navigate = useNavigate();
  const navigateToCampaign = () => navigate(`/campaigns/${campaign._id}/websites`)

  const handleProviderContentSubmit = () => {
    trackEvent('campaign_content_submitted', {
      campaignId: campaign._id,
      campaignType: campaign.type,
      contentSource: 'provider',
      step: 3,
      stepName: 'content',
    });

    trackEvent('campaign_created', {
      campaignId: campaign._id,
      campaignType: campaign.type,
      totalSteps: 3,
      websiteCount: campaign.publications?.length || 0,
      contentSource: 'provider',
      budget: campaign.targetValue || null,
    });

    navigateToCampaign();
  };

  const form = useForm({
    defaultValues: {},
    mode: "onChange",
  });

  const { notification } = AntdApp.useApp();
  // Read these during render so react-hook-form keeps the component subscribed
  // to validation changes (needed now that the button no longer reads isValid).
  const { isValid, isDirty, errors } = form.formState;

  const logContext = {
    path: 'create',
    campaignId: campaign._id,
    campaignType: campaign.type,
    contentSource: campaign.contentSource,
    status: campaign.status,
  };

  // Log the validation state / errors whenever they change, but only when they
  // actually change so we don't emit an event on every keystroke.
  const lastLoggedRef = useRef<string | null>(null);
  useEffect(() => {
    if (campaign.contentSource === CampaignContentSource.ProviderContent) return;

    const { reason, fields } = getSubmitDisabledReason(
      { isValid, isDirty, errors },
      { readOnly: false, loading, isNewEntity: true }
    );
    const serializedErrors = serializeFormErrors(errors);
    const signature = JSON.stringify({ reason, fields, serializedErrors });
    if (lastLoggedRef.current === signature) return;
    lastLoggedRef.current = signature;

    trackEvent('campaign_form_validation_state', {
      ...logContext,
      disabledReason: reason,
      invalidFields: fields,
      isValid,
      isDirty,
      errors: serializedErrors,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValid, isDirty, errors, loading]);

  if (campaign.contentSource === CampaignContentSource.ProviderContent) {
    return <ProviderContent publications={campaign.publications} submitButtonText="Create Campaign" onSubmit={handleProviderContentSubmit} />
  }

  const handleCreateCampaign = async () => {
    const valid = await form.trigger();

    trackEvent('campaign_save_attempt', {
      ...logContext,
      isValid: valid,
      errors: serializeFormErrors(form.formState.errors),
    });

    if (!valid) {
      const missing = Object.keys(form.formState.errors).map(
        (field) => FIELD_LABELS[field] || field
      );
      notification.warning({
        key: 'create-article-invalid',
        message: 'Please complete the required fields',
        description: missing.length
          ? `Still needed: ${missing.join(', ')}.`
          : 'Some required fields are missing.',
        duration: 6,
      });
      return;
    }

    const content = form.getValues();
    const record = { content };

    try {
      const res = await withAsyncErrorToast(
        'Save article',
        () => updateCampaign({ variables: { id: campaign._id, record } }),
        undefined,
        notification
      );

      if (res.data) {
        trackEvent('campaign_content_submitted', {
          campaignId: campaign._id,
          campaignType: campaign.type,
          step: 3,
          stepName: 'content',
        });

        trackEvent('campaign_created', {
          campaignId: campaign._id,
          campaignType: campaign.type,
          totalSteps: 3,
          websiteCount: campaign.publications?.length || 0,
          budget: campaign.targetValue || null,
        });

        navigateToCampaign();
      }
    } catch (error) {
      // Error notification already shown by withAsyncErrorToast; swallow it so
      // it doesn't surface as an unhandled rejection, and stay on the step so
      // the user can retry.
    }
  };

  return (
    <form>
      <FormProvider {...form}>
        <ArticleContentEditor readOnly={false} allowSponsoredLinks={campaign.type === CampaignType.Filler} />
      </FormProvider>
      <div className="my-5 px-4 py-3 text-right sm:px-6">
        <button
          type="button"
          onClick={handleCreateCampaign}
          className="py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-300 disabled:text-white disabled:cursor-not-allowed disabled:hover:bg-gray-300 disabled:opacity-100 disabled:shadow-none disabled:focus:outline-none disabled:focus:ring-0"
          disabled={loading}
        >
          Create Campaign
        </button>
      </div>
    </form>
  );
};

export default CreateMetaArticle;
